import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit3 } from 'lucide-react';
import { errorText } from '../../lib/apiErrors';
import CustomFieldRenderer from '../CustomFieldRenderer';
import CustomFieldBadge from './CustomFieldBadge';
import { missingRequiredCustomFields } from './requiredFields';

const sameValue = (a, b) => (a === b) || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Custom fields on the company, editable in place like the contact page (#972).
// `PUT /api/accounts/{id}` REPLACES the custom_fields dict, so a save sends a whole
// dict - but built at save time: the LATEST account.custom_fields with only the keys
// the user changed laid over it. A key nobody touched is never written back from a
// copy taken when Edit was pressed (another user or tab may have changed it since).
// An edit belongs to the account it started on: opening another company drops it, so
// a draft can never be saved onto the wrong record. D4: a Required field must be
// filled before it saves. `onUpdate` receives the account the PUT answered with.
export default function AccountCustomFieldsCard({ account, fields, onUpdate, getAuthHeader }) {
    // `edit` = { id, base, draft } while editing; ignored when it is for another account.
    const [edit, setEdit] = useState(null);
    const [showRequired, setShowRequired] = useState(false);
    const [saving, setSaving] = useState(false);
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    const accountId = account?.account_id;
    const editing = !!edit && edit.id === accountId;
    const draft = editing ? edit.draft : {};
    const setDraft = (next) => setEdit((e) => (e ? { ...e, draft: next } : e));

    const startEdit = () => {
        const base = { ...(account?.custom_fields || {}) };
        setEdit({ id: accountId, base, draft: { ...base } });
        setShowRequired(false);
    };
    const cancelEdit = () => { setEdit(null); setShowRequired(false); };

    const saveEdit = async () => {
        if (!editing) return;
        if (Object.keys(missingRequiredCustomFields(fields, draft)).length) {
            setShowRequired(true);
            return toast.error('Fill in the required custom fields');
        }
        const changed = {};
        Object.keys(draft).forEach((key) => { if (!sameValue(draft[key], edit.base[key])) changed[key] = draft[key]; });
        const merged = { ...(account?.custom_fields || {}), ...changed };
        setSaving(true);
        try {
            const res = await axios.put(`${API}/accounts/${accountId}`, { custom_fields: merged }, { headers: getAuthHeader() });
            toast.success('Custom fields saved');
            setEdit(null);
            setShowRequired(false);
            const saved = res?.data && typeof res.data === 'object' ? res.data : {};
            onUpdate?.({ ...saved, account_id: accountId, custom_fields: saved.custom_fields ?? merged });
        } catch (e) { toast.error(errorText(e, 'Failed to save custom fields')); }
        finally { setSaving(false); }
    };

    return (
        <div className="bg-white rounded-xl border border-stone-200" data-testid="custom-fields-section">
            <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Custom fields <CustomFieldBadge />
                </h3>
                {editing ? (
                    <div className="flex gap-2">
                        <button data-testid="custom-fields-save-btn" onClick={saveEdit} disabled={saving}
                            className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50">
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button data-testid="custom-fields-cancel-btn" onClick={cancelEdit} disabled={saving}
                            className="text-xs font-semibold text-stone-500 hover:text-stone-700">Cancel</button>
                    </div>
                ) : (
                    <button data-testid="custom-fields-edit-btn" onClick={startEdit}
                        className="flex gap-1 items-center text-xs font-medium text-stone-500 hover:text-stone-700">
                        <Edit3 className="w-3 h-3" /> Edit
                    </button>
                )}
            </div>
            <div className="p-4 sm:p-5">
                <CustomFieldRenderer
                    fields={fields}
                    values={editing ? draft : (account?.custom_fields || {})}
                    editing={editing}
                    errors={editing && showRequired ? missingRequiredCustomFields(fields, draft) : {}}
                    onChange={setDraft}
                />
            </div>
        </div>
    );
}
